<template>
    <div class="pl-show">
        <header class="pl-toolbar">
            <div class="pl-toolbar-title">
                <div class="pl-title-row">
                    <h1>{{ list.name }}</h1>
                    <span class="pl-badge" :class="list.available ? 'pl-badge--success' : 'pl-badge--outline'">
                        {{ list.available ? 'Attiva' : 'Non disponibile' }}
                    </span>
                    <span v-if="list.offer" class="pl-badge pl-badge--status">{{ label('offer', 'Offerta') }}</span>
                    <span v-if="ean.length" class="pl-badge pl-badge--neutral">{{ ean.length }} EAN</span>
                </div>
                <p>#{{ list.id ?? '—' }} · {{ list.url || '—' }}</p>
            </div>
            <div class="pl-toolbar-actions">
                <a class="pl-button pl-button--outline" :href="payload.routes.index">Indietro</a>
                <a
                    v-if="payload.routes.preview"
                    class="pl-button pl-button--outline"
                    :href="payload.routes.preview"
                    target="_blank"
                    rel="noopener noreferrer"
                >Anteprima</a>
                <a v-if="payload.permissions.can_update" class="pl-button pl-button--primary" :href="payload.routes.edit">Modifica</a>
            </div>
        </header>

        <div class="pl-content">
            <div v-if="payload.slug_conflict" role="alert" class="pl-conflict-banner">
                <span class="pl-conflict-icon" aria-hidden="true">!</span>
                <p>
                    <strong>Questo slug è usato anche dalla lista «{{ payload.slug_conflict.name }}» (#{{ payload.slug_conflict.id }})</strong>
                    — sul sito viene letta la lista con id più basso.
                    <a :href="payload.slug_conflict.show_url" class="pl-button pl-button--link">Apri</a>
                    <a :href="payload.slug_conflict.edit_url" class="pl-button pl-button--link">Modifica</a>
                </p>
            </div>

            <div class="pl-sections">
                <section class="pl-section">
                    <div class="pl-section-body">
                        <dl class="pl-show-grid">
                            <div class="pl-show-field"><dt>{{ label('name', 'Nome') }}</dt><dd>{{ display(list.name) }}</dd></div>
                            <div class="pl-show-field">
                                <dt>{{ label('url', 'Url') }}</dt>
                                <dd>
                                    <span class="pl-show-slug">
                                        <code>{{ list.url || '—' }}</code>
                                        <button v-if="list.url" type="button" class="pl-button pl-button--ghost pl-copy-button" aria-label="Copia slug" @click="copySlug">⧉</button>
                                    </span>
                                </dd>
                            </div>
                            <div class="pl-show-field"><dt>{{ label('title', 'Titolo') }}</dt><dd>{{ display(list.title) }}</dd></div>
                            <div class="pl-show-field"><dt>{{ label('description', 'Descrizione') }}</dt><dd><span class="pl-show-description">{{ display(list.description) }}</span></dd></div>
                            <div class="pl-show-field pl-show-field--full">
                                <dt>{{ label('ean', 'Articoli') }}</dt>
                                <dd>
                                    <div v-if="ean.length" class="pl-show-values">
                                        <span v-for="value in ean" :key="value" class="pl-badge pl-badge--neutral pl-show-code">{{ value }}</span>
                                    </div>
                                    <span v-else class="pl-show-empty">—</span>
                                </dd>
                            </div>
                            <div class="pl-show-field pl-show-field--full">
                                <dt>{{ label('keyword', 'Keyword') }}</dt>
                                <dd>{{ display(list.keyword) }}</dd>
                            </div>
                        </dl>
                    </div>
                </section>

                <section class="pl-section">
                    <div class="pl-section-heading"><h2>Parametri</h2></div>
                    <div class="pl-section-body">
                        <dl class="pl-show-grid">
                            <div v-for="field in paramRemoteFields" :key="field" class="pl-show-field">
                                <dt>{{ label(field, field) }}</dt>
                                <dd>
                                    <div v-if="(payload.selected[field] || []).length" class="pl-show-values">
                                        <span v-for="item in payload.selected[field]" :key="item.value" class="pl-chip">{{ item.label }}</span>
                                    </div>
                                    <span v-else class="pl-show-empty">—</span>
                                </dd>
                            </div>
                            <div class="pl-show-field">
                                <dt>{{ label('orchestra', 'Interprete') }}</dt>
                                <dd>{{ display(list.orchestra) }}</dd>
                            </div>
                            <div class="pl-show-field">
                                <dt>{{ label('property', 'Proprietà') }}</dt>
                                <dd>
                                    <div v-if="propertyItems.length" class="pl-show-values">
                                        <span v-for="item in propertyItems" :key="item.value" class="pl-chip">{{ item.label }}</span>
                                    </div>
                                    <span v-else class="pl-show-empty">—</span>
                                </dd>
                            </div>
                        </dl>
                    </div>
                </section>

                <section class="pl-section">
                    <div class="pl-section-heading"><h2>Prezzo</h2></div>
                    <div class="pl-section-body">
                        <dl class="pl-show-grid">
                            <div class="pl-show-field"><dt>{{ label('min', 'Minimo') }}</dt><dd>{{ money(list.min) }}</dd></div>
                            <div class="pl-show-field"><dt>{{ label('max', 'Massimo') }}</dt><dd>{{ money(list.max) }}</dd></div>
                        </dl>
                    </div>
                </section>

                <section class="pl-section">
                    <div class="pl-section-heading"><h2>Filtri</h2></div>
                    <div class="pl-section-body">
                        <dl class="pl-show-grid">
                            <div class="pl-show-field"><dt>{{ label('days', 'Giorni/Settimana') }}</dt><dd>{{ list.days ?? empty }}</dd></div>
                            <div class="pl-show-field"><dt>{{ label('time', 'Intervallo') }}</dt><dd>{{ timeLabel }}</dd></div>
                            <div class="pl-show-field"><dt>{{ label('limits', 'Risultati') }}</dt><dd>{{ list.limits ?? empty }}</dd></div>
                            <div class="pl-show-field">
                                <dt>{{ label('offer', 'Offerta') }}</dt>
                                <dd><span class="pl-badge" :class="list.offer ? 'pl-badge--success' : 'pl-badge--outline'">{{ list.offer ? 'Sì' : 'No' }}</span></dd>
                            </div>
                            <div class="pl-show-field">
                                <dt>{{ label('available', 'Disponibile') }}</dt>
                                <dd><span class="pl-badge" :class="list.available ? 'pl-badge--success' : 'pl-badge--outline'">{{ list.available ? 'Sì' : 'No' }}</span></dd>
                            </div>
                            <div class="pl-show-field"><dt>{{ label('sort', 'Ordina') }}</dt><dd>{{ sortLabel }}</dd></div>
                        </dl>
                    </div>
                </section>

                <section v-if="showCategory" class="pl-section">
                    <div class="pl-section-heading"><h2>Categorie</h2></div>
                    <div class="pl-section-body">
                        <dl class="pl-show-grid">
                            <div class="pl-show-field pl-show-field--full">
                                <dt>{{ label('category', 'Categorie') }}</dt>
                                <dd>
                                    <div v-if="(payload.selected.category || []).length" class="pl-show-values">
                                        <span v-for="item in payload.selected.category" :key="item.value" class="pl-chip">{{ item.label }}</span>
                                    </div>
                                    <span v-else class="pl-show-empty">—</span>
                                </dd>
                            </div>
                        </dl>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import { catalogRemoteFieldsFor, remoteFieldsFor } from '../normalizeProductListPayload';

export default {
    name: 'ProductListShowCard',
    props: {
        payload: { type: Object, required: true },
    },
    emits: ['copied'],
    computed: {
        list() {
            return this.payload.list;
        },
        ean() {
            return (this.list.ean || []).filter(Boolean);
        },
        paramRemoteFields() {
            return catalogRemoteFieldsFor(this.payload);
        },
        showCategory() {
            return remoteFieldsFor(this.payload).includes('category');
        },
        propertyItems() {
            const options = this.payload.options.property || {};

            return (this.list.property || []).map((value) => ({
                value,
                label: options[value] || value,
            }));
        },
        timeLabel() {
            if (!this.list.time) {
                return this.empty;
            }

            return (this.payload.options.time || {})[this.list.time] || this.list.time;
        },
        sortLabel() {
            return (this.payload.options.sort || {})[this.list.sort] || this.list.sort || this.empty;
        },
        empty() {
            return '—';
        },
    },
    methods: {
        label(field, fallback) {
            return this.payload.labels[field] || fallback;
        },
        display(value) {
            return value || this.empty;
        },
        money(value) {
            return `€ ${new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0)}`;
        },
        async copySlug() {
            await navigator.clipboard.writeText(this.list.url);
            this.$emit('copied', 'Slug copiato');
        },
    },
};
</script>
